import React, { useRef, useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useGlobalContext } from 'contexts/global-context';
import useThrottleInstallGameClick from 'hooks/useThrottleInstallGameClick';
import Game from 'components/game';
import Video from 'components/video';
import { TGame, ABTest } from 'types';
import './styles.scss';

export enum MainItemPosition {
  bottomRight = 'bottom-right',
  bottomLeft = 'bottom-left',
  topRight = 'top-right',
  topLeft = 'top-left',
}

interface IGridGames {
  games: TGame[],
  className?: string;
  selectedGame?: TGame,
  mainItemPosition?: MainItemPosition,
  onGameClick?: (game: TGame) => void;
}

const GridGames: React.FC<IGridGames>= ({
  games,
  className,
  selectedGame,
  mainItemPosition,
  onGameClick,
}) => {
  const { abTestId } = useGlobalContext();
  const ref = useRef<HTMLDivElement>();
  const { ref: inViewRef, inView } = useInView({ threshold: 0.6 });
  const [mainGame, setMainGame] = useState<TGame>(games[0]);

  // By the agreement, the first game is considered as a Main game if not selected. BE returns games already sorted.
  const onMainGameClick = useThrottleInstallGameClick(mainGame);

  const setContainerRefs = useCallback((node: HTMLDivElement) => {
    ref.current = node;
    inViewRef(node);
  }, [inViewRef]);

  const handleClick = (game: TGame) => {
    if (abTestId === ABTest.switchMainVideo) {
      setMainGame(game);

      if (!inView && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (onGameClick) {
      onGameClick(game);
    }
  };

  return (
    <div ref={setContainerRefs} className={`grid ${className || ''} ${mainItemPosition}-main`}>
      {games.map((game, index) => (
        <React.Fragment key={game.id}>
          {game.id === mainGame?.id && (
            <div className="main-item relative">
              {game.videoShort && (
                <Video
                  muted
                  autoRepeat
                  autoPlayInView
                  hideVolumeControls
                  withControls={false}
                  url={game.videoShort}
                  poster={game.videoPoster}
                  handleClick={onMainGameClick}
                />
              )}
              <Game compact aboveParent game={game} />
            </div>
          )}
          {(abTestId === ABTest.switchMainVideo || game.id !== mainGame?.id) && (
            <Game
              game={game}
              selected={game.id === selectedGame?.id}
              main={abTestId === ABTest.switchMainVideo && game.id === mainGame?.id}
              aboveInstall={[ABTest.autoShowGetBtnOnGame, ABTest.showGetBtnOnGame].includes(abTestId as ABTest)}
              onClick={[ABTest.switchMainVideo, ABTest.autoShowGetBtnOnGame, ABTest.showGetBtnOnGame].includes(abTestId as ABTest) ? handleClick : undefined}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default GridGames;
